import { useEffect } from "react"
import { useForm, useFieldArray } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import { useQuery, useMutation } from "urql"
import { toast } from "sonner"
import { usePostHog } from "@posthog/react"
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Textarea } from "@/components/ui/textarea"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Checkbox } from "@/components/ui/checkbox"
import { Switch } from "@/components/ui/switch"
import { Loader2, Plus, X } from "lucide-react"
import { FieldError } from "@/components/shared/field-error"
import { subscriptionSchema, type SubscriptionFormValues } from "@/lib/schemas"
import { SourcesQuery, DestinationsQuery, TransformationsQuery } from "@/lib/graphql/queries"
import { CreateSubscriptionMutation, UpdateSubscriptionMutation } from "@/lib/graphql/mutations"

const BODY_MATCH_OPERATORS = [
  { value: "eq", label: "Equals" },
  { value: "neq", label: "Not Equals" },
  { value: "contains", label: "Contains" },
  { value: "exists", label: "Exists" },
  { value: "not_exists", label: "Not Exists" },
] as const

const FILTER_RULE_OPERATORS = [
  { value: "$eq", label: "=" },
  { value: "$neq", label: "!=" },
  { value: "$gt", label: ">" },
  { value: "$gte", label: ">=" },
  { value: "$lt", label: "<" },
  { value: "$lte", label: "<=" },
  { value: "$in", label: "in" },
  { value: "$nin", label: "not in" },
  { value: "$regex", label: "regex" },
  { value: "$exists", label: "exists" },
] as const

function parseFilterValue(operator: string, raw: string): unknown {
  if (!raw) return undefined
  if (operator === "$exists") return raw === "true"
  if (operator === "$in" || operator === "$nin") {
    return raw.split(",").map((s) => {
      const trimmed = s.trim()
      const n = Number(trimmed)
      return isNaN(n) ? trimmed : n
    })
  }
  const n = Number(raw)
  if (!isNaN(n) && raw.trim() !== "") return n
  return raw
}

interface SubscriptionFormProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  onSuccess: () => void
  editId?: string
  defaultValues?: SubscriptionFormValues
}

export function SubscriptionForm({ open, onOpenChange, onSuccess, editId, defaultValues }: SubscriptionFormProps) {
  const posthog = usePostHog()
  const isEdit = !!editId
  const [sources] = useQuery({ query: SourcesQuery })
  const [destinations] = useQuery({ query: DestinationsQuery })
  const [transformations] = useQuery({ query: TransformationsQuery })
  const [, create] = useMutation(CreateSubscriptionMutation)
  const [, update] = useMutation(UpdateSubscriptionMutation)

  const form = useForm<SubscriptionFormValues>({
    resolver: zodResolver(subscriptionSchema),
    defaultValues: defaultValues ?? { name: "", sourceId: "", destinationId: "", eventTypes: "", regex: "", bodyMatchRules: [], filterRules: [], envelope: false, transformHeaders: "", transformationId: "", delay: "", channels: "" },
  })

  const { fields: bodyMatchFields, append: addBodyMatch, remove: removeBodyMatch } = useFieldArray({
    control: form.control,
    name: "bodyMatchRules",
  })

  const { fields: filterRuleFields, append: addFilterRule, remove: removeFilterRule } = useFieldArray({
    control: form.control,
    name: "filterRules",
  })

  useEffect(() => {
    if (open && defaultValues) {
      form.reset(defaultValues)
    }
  }, [open, defaultValues, form])

  const onSubmit = async (values: SubscriptionFormValues) => {
    const eventTypes = values.eventTypes
      ? values.eventTypes.split(",").map((s) => s.trim()).filter(Boolean)
      : undefined

    const regex = values.regex
      ? values.regex.split("\n").map((s) => s.trim()).filter(Boolean)
      : undefined

    const bodyMatch = values.bodyMatchRules?.length
      ? values.bodyMatchRules.map((r) => ({ path: r.path, value: r.value, operator: r.operator }))
      : undefined

    const rules = values.filterRules?.length
      ? values.filterRules.map((r) => ({
          path: r.path || undefined,
          operator: r.operator,
          value: (r.value !== undefined ? parseFilterValue(r.operator, r.value) : undefined) as Record<string, unknown> | undefined,
        }))
      : undefined

    const hasFilter = (eventTypes && eventTypes.length > 0) || (regex && regex.length > 0) || (bodyMatch && bodyMatch.length > 0) || (rules && rules.length > 0)

    let transformHeaders: Record<string, string> | undefined
    if (values.transformHeaders) {
      try {
        transformHeaders = JSON.parse(values.transformHeaders)
      } catch {
        toast.error("Transform headers must be valid JSON")
        return
      }
    }

    const transformConfig = (values.envelope || transformHeaders)
      ? { envelope: values.envelope ?? false, headers: transformHeaders }
      : undefined

    const filterConfig = hasFilter ? { eventTypes, regex, bodyMatch, rules } : undefined

    const delay = values.delay || undefined
    const channels = values.channels ? values.channels.split(",").map((s) => s.trim()).filter(Boolean) : undefined
    const transformationId = values.transformationId || undefined

    if (isEdit) {
      const result = await update({
        id: editId,
        input: {
          name: values.name,
          enabled: values.enabled,
          filterConfig: filterConfig ?? null,
          transformConfig,
          transformationId: transformationId ?? null,
          delay,
          channels,
        },
      })
      if (result.error) { toast.error(result.error.message); return }
      posthog?.capture("subscription_updated", { has_filter: !!filterConfig })
      toast.success("Subscription updated")
    } else {
      const result = await create({
        input: {
          name: values.name,
          sourceId: values.sourceId,
          destinationId: values.destinationId,
          filterConfig,
          transformConfig,
          transformationId,
          delay,
          channels,
        },
      })
      if (result.error) { toast.error(result.error.message); return }
      posthog?.capture("subscription_created", { has_filter: !!filterConfig, source_id: values.sourceId, destination_id: values.destinationId })
      toast.success("Subscription created")
    }

    onSuccess()
    onOpenChange(false)
    form.reset()
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{isEdit ? "Edit" : "Create"} Subscription</DialogTitle>
        </DialogHeader>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
          <div className="space-y-4 rounded-lg glass-card p-4">
            <div className="space-y-2">
              <Label htmlFor="sub-name">Name</Label>
              <Input id="sub-name" {...form.register("name")} />
              <FieldError error={form.formState.errors.name} />
            </div>

            {isEdit && (
              <div className="flex items-center justify-between">
                <Label htmlFor="sub-enabled" className="cursor-pointer">Enabled</Label>
                <Switch
                  id="sub-enabled"
                  checked={form.watch("enabled") ?? true}
                  onCheckedChange={(checked) => form.setValue("enabled", checked)}
                />
              </div>
            )}

            <div className="space-y-2">
              <Label>Source</Label>
              <Select
                value={form.watch("sourceId")}
                onValueChange={(v) => form.setValue("sourceId", v, { shouldValidate: true })}
                disabled={isEdit}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select source..." />
                </SelectTrigger>
                <SelectContent>
                  {sources.data?.sources.nodes.map((s) => (
                    <SelectItem key={s.id} value={s.id}>
                      {s.name} ({s.slug})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <FieldError error={form.formState.errors.sourceId} />
            </div>

            <div className="space-y-2">
              <Label>Destination</Label>
              <Select
                value={form.watch("destinationId")}
                onValueChange={(v) => form.setValue("destinationId", v, { shouldValidate: true })}
                disabled={isEdit}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select destination..." />
                </SelectTrigger>
                <SelectContent>
                  {destinations.data?.destinations.nodes.map((d) => (
                    <SelectItem key={d.id} value={d.id}>
                      {d.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <FieldError error={form.formState.errors.destinationId} />
            </div>
          </div>

          <div className="space-y-4">
            <Label className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Filters</Label>

            <div className="space-y-2">
              <Label htmlFor="eventTypes">Event Types (comma-separated)</Label>
              <Input
                id="eventTypes"
                className="font-mono text-sm"
                placeholder="invoice.*, payment.completed"
                {...form.register("eventTypes")}
              />
              <p className="text-xs text-muted-foreground">Wildcard matching. Leave empty for all event types.</p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="regex">Regex Patterns (one per line)</Label>
              <Textarea
                id="regex"
                className="font-mono text-sm min-h-[4.5rem]"
                placeholder={"^order\\..*$\n^payment\\.(success|failed)$"}
                rows={3}
                {...form.register("regex")}
              />
              <p className="text-xs text-muted-foreground">Event types matching any pattern will be routed.</p>
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label>Body Match Rules</Label>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => addBodyMatch({ path: "", operator: "eq", value: "" })}
                >
                  <Plus className="h-3.5 w-3.5 mr-1.5" />
                  Add Rule
                </Button>
              </div>
              {bodyMatchFields.length > 0 && (
                <div className="space-y-2">
                  {bodyMatchFields.map((field, index) => (
                    <div key={field.id} className="flex items-start gap-2 rounded-md border border-border/40 p-2">
                      <div className="flex-1 space-y-2">
                        <Input
                          className="font-mono text-sm"
                          placeholder="$.data.type"
                          {...form.register(`bodyMatchRules.${index}.path`)}
                        />
                        <div className="flex gap-2">
                          <Select
                            value={form.watch(`bodyMatchRules.${index}.operator`)}
                            onValueChange={(v) => form.setValue(`bodyMatchRules.${index}.operator` as const, v as "eq" | "neq" | "contains" | "exists" | "not_exists")}
                          >
                            <SelectTrigger className="w-32">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {BODY_MATCH_OPERATORS.map((op) => (
                                <SelectItem key={op.value} value={op.value}>{op.label}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <Input
                            className="flex-1 font-mono text-sm"
                            placeholder="value"
                            {...form.register(`bodyMatchRules.${index}.value`)}
                            disabled={form.watch(`bodyMatchRules.${index}.operator`) === "exists" || form.watch(`bodyMatchRules.${index}.operator`) === "not_exists"}
                          />
                        </div>
                      </div>
                      <Button type="button" variant="ghost" size="sm" onClick={() => removeBodyMatch(index)} className="mt-1">
                        <X className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
              <p className="text-xs text-muted-foreground">Match JSON body fields using JSONPath expressions.</p>
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label>Advanced Filter Rules</Label>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => addFilterRule({ path: "body.", operator: "$eq", value: "" })}
                >
                  <Plus className="h-3.5 w-3.5 mr-1.5" />
                  Add Rule
                </Button>
              </div>
              {filterRuleFields.length > 0 && (
                <div className="space-y-2">
                  {filterRuleFields.map((field, index) => {
                    const op = form.watch(`filterRules.${index}.operator`)
                    const isExistsOp = op === "$exists"
                    const isListOp = op === "$in" || op === "$nin"
                    return (
                      <div key={field.id} className="flex items-start gap-2 rounded-md border border-border/40 p-2">
                        <div className="flex-1 space-y-2">
                          <Input
                            className="font-mono text-sm"
                            placeholder="body.data.status or headers.x-custom or event_type"
                            {...form.register(`filterRules.${index}.path`)}
                          />
                          <div className="flex gap-2">
                            <Select
                              value={form.watch(`filterRules.${index}.operator`)}
                              onValueChange={(v) => form.setValue(`filterRules.${index}.operator` as const, v as typeof FILTER_RULE_OPERATORS[number]["value"])}
                            >
                              <SelectTrigger className="w-24">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                {FILTER_RULE_OPERATORS.map((op) => (
                                  <SelectItem key={op.value} value={op.value}>{op.label}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                            <Input
                              className="flex-1 font-mono text-sm"
                              placeholder={isListOp ? "val1, val2, val3" : isExistsOp ? "true" : "value"}
                              {...form.register(`filterRules.${index}.value`)}
                              disabled={isExistsOp}
                            />
                          </div>
                        </div>
                        <Button type="button" variant="ghost" size="sm" onClick={() => removeFilterRule(index)} className="mt-1">
                          <X className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    )
                  })}
                </div>
              )}
              <p className="text-xs text-muted-foreground">
                Operator-based filters on body, headers, or event_type. Paths: body.field, headers.name, event_type.
              </p>
            </div>
          </div>

          <div className="space-y-3">
            <Label className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Transforms</Label>
            <div className="flex items-center gap-2">
              <Checkbox
                id="envelope"
                checked={form.watch("envelope") ?? false}
                onCheckedChange={(checked: boolean | "indeterminate") => form.setValue("envelope", checked === true)}
              />
              <Label htmlFor="envelope" className="text-sm font-normal cursor-pointer">
                Wrap payload in metadata envelope
              </Label>
            </div>
            <div className="space-y-2">
              <Label htmlFor="transformHeaders">Custom Headers (JSON)</Label>
              <Input
                id="transformHeaders"
                className="font-mono text-sm"
                placeholder='{"X-Custom-Route": "v2"}'
                {...form.register("transformHeaders")}
              />
            </div>
            <div className="space-y-2">
              <Label>Transformation</Label>
              <Select
                value={form.watch("transformationId") ?? ""}
                onValueChange={(v) => form.setValue("transformationId", v === "__none__" ? "" : v)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="None" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">None</SelectItem>
                  {transformations.data?.transformations.nodes.map((t) => (
                    <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">Apply a transformation to the payload before delivery</p>
            </div>
          </div>

          <div className="space-y-3">
            <Label className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Delivery Options</Label>
            <div className="space-y-2">
              <Label htmlFor="delay">Delay</Label>
              <Input id="delay" className="font-mono" placeholder="30s, 5m" {...form.register("delay")} />
              <p className="text-xs text-muted-foreground">Delay delivery by this duration after receiving the event</p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="channels">Channels (comma-separated)</Label>
              <Input id="channels" className="font-mono text-sm" placeholder="channel-1, channel-2" {...form.register("channels")} />
              <p className="text-xs text-muted-foreground">Route to specific delivery channels</p>
            </div>
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button type="submit" disabled={form.formState.isSubmitting}>
              {form.formState.isSubmitting ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : isEdit ? "Save" : "Create"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  )
}
